import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollShadow,
  useDisclosure,
} from "@heroui/react";
import { useMediaQuery } from "@mantine/hooks";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { ArrowRight, Bell, BellOff } from "lucide-react";
// import { AlertTriangle, X } from "lucide-react";
// import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
// import { createPortal } from "react-dom";
import { useClientFeature } from "@/hooks/useClientFeature";
import { useSidebarStore } from "@/stores/useSidebarStore";
import { cn } from "@/utils";
import { NotificationCard } from "@/pages/Dashboard/Notifications/components/NotificationCard";
import { NotificationDrawer } from "@/pages/Dashboard/Notifications/components/NotificationDrawer";
// import { NotificationMessage } from "@/pages/Dashboard/Notifications/components/NotificationMessage";
import { useNotifications } from "@/pages/Dashboard/Notifications/hooks/useNotifications";
// import { formatRelativeNotificationDate } from "@/pages/Dashboard/Notifications/services/notifications";
import { formatUnreadCount } from "./formatUnreadCount";

const POPOVER_PREVIEW_LIMIT = 4;

export const NotificationsButton = () => {
  const sidebarOpen = useSidebarStore((state) => state.isOpen);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const { isFeatureEnabled } = useClientFeature();
  const isInAppNotificationsEnabled = isFeatureEnabled("inAppNotifications");

  const [isNotificationsPopoverOpen, setIsNotificationsPopoverOpen] =
    useState(false);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  const {
    isOpen: isNotificationsDrawerOpen,
    onClose: onCloseNotificationsDrawer,
    onOpen: onOpenNotificationsDrawer,
    onOpenChange: onOpenChangeNotificationsDrawer,
  } = useDisclosure();

  const {
    notifications,
    unreadCount,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isMarkingAsRead,
    markAsRead,
    markAllAsRead,
    // incomingNotificationPopup,
    // dismissIncomingNotificationPopup,
  } = useNotifications({ enabled: isInAppNotificationsEnabled });

  const unreadLabel = useMemo(
    () => formatUnreadCount(unreadCount),
    [unreadCount],
  );
  const previewNotifications = useMemo(
    () => notifications.slice(0, POPOVER_PREVIEW_LIMIT),
    [notifications],
  );

  // HeroUI popovers share a global overlay stack and only dismiss via
  // outside-press while they are the topmost overlay. An open Tooltip elsewhere
  // (e.g. a calendar appointment's hover tooltip) sits on top of that stack and
  // silently blocks the dismissal, leaving this popover open over whatever the
  // click opened. Close it ourselves on any genuine outside pointer-down —
  // ignoring presses on the trigger (it toggles itself) and inside the content.
  useEffect(() => {
    if (!isNotificationsPopoverOpen) return;

    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (
        !target ||
        popoverContentRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return;
      }
      setIsNotificationsPopoverOpen(false);
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown, true);
    return () =>
      document.removeEventListener(
        "pointerdown",
        handleOutsidePointerDown,
        true,
      );
  }, [isNotificationsPopoverOpen]);

  if (!isInAppNotificationsEnabled) {
    return null;
  }

  const hasUnread = unreadCount > 0;

  const triggerButton = (
    <Button
      ref={triggerRef}
      className={cn(
        "w-full shrink-0 justify-start rounded-4xl px-3 text-gray-500",
        { "rounded-3xl": sidebarOpen },
      )}
      variant="light"
      isIconOnly={!sidebarOpen}
      size="sm"
      aria-label={t`Obvestila`}
      onPress={isMobile ? onOpenNotificationsDrawer : undefined}
    >
      <div className="flex w-full items-center gap-2">
        <div className="relative">
          <Bell size={16} className="-ml-0.5 shrink-0" />

          {hasUnread && (
            <span
              className={cn(
                "bg-danger absolute -top-1 -right-1 size-2 rounded-full",
                { "animate-pulse": hasUnread },
              )}
              aria-hidden
            />
          )}
        </div>

        {sidebarOpen && (
          <>
            <span className="text-xs font-medium text-gray-500">
              <Trans>Obvestila</Trans>
            </span>

            {hasUnread && (
              <span className="bg-danger ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] leading-none font-semibold text-white">
                {unreadLabel}
              </span>
            )}
          </>
        )}
      </div>
    </Button>
  );

  return (
    <>
      {isMobile ? (
        triggerButton
      ) : (
        <Popover
          placement={sidebarOpen ? "top-end" : "right-end"}
          isOpen={isNotificationsPopoverOpen}
          onOpenChange={setIsNotificationsPopoverOpen}
          offset={12}
          classNames={{
            content: "p-0 border-divider border shadow-2xl",
          }}
        >
          <PopoverTrigger>{triggerButton}</PopoverTrigger>

          <PopoverContent>
            <div
              ref={popoverContentRef}
              className="bg-background rounded-large flex w-[360px] flex-col overflow-hidden"
            >
              <div className="border-divider flex items-center justify-between border-b px-4 py-3">
                <div className="flex items-baseline gap-2">
                  <h3 className="text-foreground text-sm font-semibold">
                    <Trans>Notifications</Trans>
                  </h3>
                  {hasUnread && (
                    <span className="text-default-500 text-xs">
                      {unreadLabel} <Trans>new</Trans>
                    </span>
                  )}
                </div>

                {hasUnread && (
                  <Button
                    size="sm"
                    variant="light"
                    className="text-default-500 h-7 text-xs"
                    isDisabled={isMarkingAsRead}
                    onPress={() => markAllAsRead()}
                  >
                    <Trans>Označi vse kot prebrano</Trans>
                  </Button>
                )}
              </div>

              <ScrollShadow
                hideScrollBar
                className="flex max-h-[360px] flex-col"
              >
                {isLoading ? (
                  <div className="text-default-500 px-4 py-8 text-center text-sm">
                    <Trans>Nalaganje...</Trans>
                  </div>
                ) : previewNotifications.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
                    <div className="bg-default-100 flex h-10 w-10 items-center justify-center rounded-full">
                      <BellOff size={16} className="text-default-400" />
                    </div>
                    <p className="text-foreground text-sm font-medium">
                      <Trans>Vse je prebrano</Trans>
                    </p>
                    <p className="text-default-400 text-xs">
                      <Trans>Ni novih obvestil</Trans>
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 px-3 py-3">
                    {previewNotifications.map((notification) => (
                      <NotificationCard
                        key={notification.id}
                        notification={notification}
                        isMarkingAsRead={isMarkingAsRead}
                        onMarkAsRead={markAsRead}
                        closeDrawer={() => setIsNotificationsPopoverOpen(false)}
                      />
                    ))}
                  </div>
                )}
              </ScrollShadow>

              <div className="border-divider border-t px-3 py-2">
                <Button
                  variant="light"
                  size="sm"
                  className="text-default-600 w-full justify-between"
                  endContent={<ArrowRight size={14} />}
                  onPress={() => {
                    setIsNotificationsPopoverOpen(false);
                    onOpenNotificationsDrawer();
                  }}
                >
                  <Trans>Poglej vsa obvestila</Trans>
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* {createPortal(
        <AnimatePresence initial={false}>
          {incomingNotificationPopup && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.96 }}
              transition={{ duration: 0.2, ease: "circOut" }}
              className="pointer-events-none fixed top-4 right-4 z-9999999999"
            >
              <div
                className={cn(
                  "bg-background rounded-large pointer-events-auto w-80 p-3 shadow-2xl ring-1",
                  {
                    "ring-danger/30 shadow-danger/20":
                      incomingNotificationPopup.type === "alert",
                    "ring-primary/30 shadow-primary/20":
                      incomingNotificationPopup.type !== "alert",
                  },
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="relative shrink-0">
                    <div
                      className={cn(
                        "rounded-medium flex h-9 w-9 items-center justify-center",
                        {
                          "bg-danger-100 text-danger":
                            incomingNotificationPopup.type === "alert",
                          "bg-primary-100 text-primary":
                            incomingNotificationPopup.type !== "alert",
                        },
                      )}
                      aria-hidden
                    >
                      {incomingNotificationPopup.type === "alert" ? (
                        <AlertTriangle size={16} />
                      ) : (
                        <Bell size={16} />
                      )}
                    </div>
                    <span
                      className={cn(
                        "ring-background absolute -top-0.5 -right-0.5 size-2.5 rounded-full ring-2",
                        {
                          "bg-danger":
                            incomingNotificationPopup.type === "alert",
                          "bg-primary":
                            incomingNotificationPopup.type !== "alert",
                        },
                      )}
                      aria-hidden
                    />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-foreground text-sm leading-tight font-semibold">
                        {incomingNotificationPopup.title}
                      </p>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        radius="full"
                        className="-mt-1 -mr-1 h-6 w-6 min-w-6"
                        onPress={dismissIncomingNotificationPopup}
                        aria-label={t`Zapusti`}
                      >
                        <X size={14} />
                      </Button>
                    </div>

                    <NotificationMessage
                      notification={{
                        ...incomingNotificationPopup,
                        isRead: false,
                      }}
                      className="mt-1 line-clamp-4"
                      onAfterNavigate={dismissIncomingNotificationPopup}
                    />

                    <p className="text-default-400 mt-2 text-[11px]">
                      {formatRelativeNotificationDate(
                        incomingNotificationPopup.createdAt,
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )} */}

      <NotificationDrawer
        isOpen={isNotificationsDrawerOpen}
        onClose={onCloseNotificationsDrawer}
        onOpenChange={onOpenChangeNotificationsDrawer}
        notifications={notifications}
        unreadCount={unreadCount}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={() => void fetchNextPage()}
        isMarkingAsRead={isMarkingAsRead}
        onMarkAsRead={markAsRead}
        onMarkAllAsRead={markAllAsRead}
      />
    </>
  );
};
